import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronUp, Loader2, RefreshCw, Square, X } from "lucide-react";

import { api, type Camera, type SpatialFilter } from "../api";
import { InfoHint } from "./InfoHint";
import { SearchableSelect } from "./SearchableSelect";

// Spatial search: find objects that were physically inside a region of one
// camera's view.
//
// Coordinates are normalized against the DISPLAYED image box and compared
// server-side against detection boxes normalized the same way, so the region
// means the same thing whether the frame is drawn 400px or 1400px wide, and
// whatever resolution the camera runs at. That is why the frame is rendered at
// its natural aspect ratio with no letterboxing: the element's own rectangle
// is then exactly the picture, and the mapping is a straight divide.

const DEFAULT_OVERLAP = 0.5;
/** Midday, when nothing else is chosen: the middle of a trading day is the
 *  most useful frame to reason about, and an empty time field is not. */
const DEFAULT_HOUR = 12;

// `.bf-input` is `w-full`, which stretches every control to the row when they
// are laid out side by side instead of stacked — each one carries its own
// width so the whole filter fits on the label's line.
const CONTROL = "bf-input px-2 py-1.5 text-xs text-bf-muted";

function defaultDateValue(): string {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function defaultTimeValue(): string {
  return `${String(DEFAULT_HOUR).padStart(2, "0")}:00`;
}

function toDate(dateStr: string, timeStr: string): Date {
  const [h, min] = (timeStr || defaultTimeValue()).split(":").map(Number);
  const [y, m, d] = (dateStr || defaultDateValue()).split("-").map(Number);
  return new Date(y, (m || 1) - 1, d || 1, h || 0, min || 0, 0, 0);
}

type Box = { x1: number; y1: number; x2: number; y2: number };

function normalizeBox(b: Box): Box {
  return {
    x1: Math.min(b.x1, b.x2),
    y1: Math.min(b.y1, b.y2),
    x2: Math.max(b.x1, b.x2),
    y2: Math.max(b.y1, b.y2),
  };
}

export function SpatialFilterPanel({
  cameras,
  value,
  onChange,
}: {
  cameras: Camera[];
  value: SpatialFilter | null;
  onChange: (next: SpatialFilter | null) => void;
}) {
  const [cameraId, setCameraId] = useState(value?.camera_id ?? "");
  const [date, setDate] = useState(defaultDateValue);
  const [time, setTime] = useState(defaultTimeValue);
  const [overlap, setOverlap] = useState(value?.min_overlap ?? DEFAULT_OVERLAP);
  const [overlapText, setOverlapText] = useState(
    String(value?.min_overlap ?? DEFAULT_OVERLAP),
  );
  const [open, setOpen] = useState(Boolean(value));
  const [box, setBox] = useState<Box | null>(
    value
      ? {
          x1: value.bbox[0],
          y1: value.bbox[1],
          x2: value.bbox[2],
          y2: value.bbox[3],
        }
      : null,
  );
  const [drag, setDrag] = useState<Box | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  // Default the camera to the first available one so the panel is usable the
  // moment it opens rather than presenting three empty controls.
  useEffect(() => {
    if (!cameraId && cameras.length > 0) setCameraId(cameras[0].id);
  }, [cameraId, cameras]);

  const at = useMemo(() => toDate(date, time), [date, time]);

  const frame = useQuery({
    queryKey: ["frame-at", cameraId, at.toISOString()],
    queryFn: () => api.frameAt(cameraId, at),
    enabled: Boolean(cameraId) && open,
    retry: false,
    staleTime: 60_000,
  });

  // Emit upward whenever the region is complete. A region without a camera, or
  // a zero-area drag, is not a filter — it is an unfinished gesture.
  useEffect(() => {
    if (!cameraId || !box) {
      onChange(null);
      return;
    }
    const n = normalizeBox(box);
    if (n.x2 - n.x1 < 0.01 || n.y2 - n.y1 < 0.01) {
      onChange(null);
      return;
    }
    onChange({
      camera_id: cameraId,
      bbox: [n.x1, n.y1, n.x2, n.y2],
      min_overlap: overlap,
      at_ts: at.toISOString(),
    });
    // `onChange` is a fresh closure on every parent render; depending on it
    // would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cameraId, box, overlap, at]);

  const pointFromEvent = useCallback((e: React.PointerEvent): { x: number; y: number } => {
    const rect = imgRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };
    return {
      x: Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (e.clientY - rect.top) / rect.height)),
    };
  }, []);

  const onPointerDown = (e: React.PointerEvent) => {
    if (!frame.data) return;
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    const p = pointFromEvent(e);
    setDrag({ x1: p.x, y1: p.y, x2: p.x, y2: p.y });
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag) return;
    const p = pointFromEvent(e);
    setDrag({ ...drag, x2: p.x, y2: p.y });
  };

  const onPointerUp = () => {
    if (!drag) return;
    const n = normalizeBox(drag);
    setDrag(null);
    // A click (rather than a drag) clears the region instead of leaving a
    // sliver nobody meant to draw.
    setBox(n.x2 - n.x1 < 0.01 || n.y2 - n.y1 < 0.01 ? null : n);
  };

  const commitOverlap = (raw: string) => {
    const n = Number(raw);
    const next = Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : DEFAULT_OVERLAP;
    setOverlap(next);
    setOverlapText(String(next));
  };

  const shown = drag ? normalizeBox(drag) : box;
  const cameraName =
    cameras.find((c) => c.id === cameraId)?.name || cameraId || "no camera";

  return (
    <div className="space-y-3">
      {/* Every control sits on the label's own row: the region is one filter,
          not a sub-form, and the frame below is the only part worth hiding. */}
      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-2">
        <span className="whitespace-nowrap text-sm font-medium text-bf-text">
          Spot search
        </span>
        <InfoHint>
          Find people who were physically inside part of a camera&apos;s view.
          Load a frame from the time you care about, drag a box over the area,
          and set how much of a person has to be inside it to count. Overlap is
          measured against each person&apos;s own size, so someone half in the
          area counts as 0.5 — 0.1 catches anyone who passed through, 0.9 only
          those fully within it. The frame is only used for aiming; the search
          still covers the whole time range.
        </InfoHint>

        <SearchableSelect
          className="w-36"
          ariaLabel="Spot search camera"
          value={cameraId}
          onChange={(v) => {
            setCameraId(v);
            setBox(null);
            setOpen(true);
          }}
          placeholder="Camera…"
          options={cameras.map((c) => ({
            value: c.id,
            label: c.name || c.id,
            hint: c.store || undefined,
            keywords: c.id,
          }))}
        />
        <input
          type="date"
          aria-label="Frame date"
          className={CONTROL + " w-[8.5rem]"}
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <input
          type="time"
          aria-label="Frame time"
          className={CONTROL + " w-[6.5rem]"}
          value={time}
          onChange={(e) => setTime(e.target.value)}
        />
        <label className="flex items-center gap-1 whitespace-nowrap text-xs text-bf-muted">
          Overlap ≥
          <input
            type="number"
            min={0}
            max={1}
            step={0.05}
            aria-label="Minimum overlap"
            className={CONTROL + " w-16 text-bf-text"}
            value={overlapText}
            onChange={(e) => setOverlapText(e.target.value)}
            onBlur={(e) => commitOverlap(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") commitOverlap((e.target as HTMLInputElement).value);
            }}
          />
        </label>
        <button
          type="button"
          className="bf-button whitespace-nowrap bg-white/10 bg-none px-2.5 py-1.5 text-xs disabled:opacity-40"
          onClick={() => {
            setOpen(true);
            void frame.refetch();
          }}
          disabled={!cameraId || frame.isFetching}
        >
          <RefreshCw
            size={13}
            className={"mr-1.5 " + (frame.isFetching ? "animate-spin" : "")}
          />
          Load frame
        </button>
        {box && (
          <button
            type="button"
            className="flex items-center gap-1 whitespace-nowrap text-xs text-bf-muted hover:text-bf-text"
            onClick={() => setBox(null)}
          >
            <X size={13} /> Clear
          </button>
        )}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-label={open ? "Hide frame" : "Show frame"}
          className="flex items-center gap-1 text-xs text-bf-muted hover:text-bf-text"
        >
          {open ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
        </button>
      </div>

      {open && (
        <div className="relative select-none overflow-hidden rounded-lg border border-white/10 bg-white/[0.03]">
          {!cameraId ? (
            <p className="py-16 text-center text-sm text-bf-muted">
              Pick a camera to load a frame.
            </p>
          ) : frame.isLoading || frame.isFetching ? (
            <p className="flex items-center justify-center gap-2 py-16 text-sm text-bf-muted">
              <Loader2 size={16} className="animate-spin" /> Loading a frame from{" "}
              {cameraName}…
            </p>
          ) : frame.isError || !frame.data ? (
            <p className="px-4 py-16 text-center text-sm text-bf-muted">
              {(frame.error as Error)?.message ||
                "No frame available for that camera and time."}
            </p>
          ) : (
            <div
              className="relative cursor-crosshair touch-none"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <img
                ref={imgRef}
                src={frame.data.frame_url}
                alt={`Frame from ${cameraName}`}
                className="block w-full select-none"
                draggable={false}
              />
              {shown && (
                <div
                  className="pointer-events-none absolute border-2 border-bf-magenta bg-bf-magenta/15"
                  style={{
                    left: `${shown.x1 * 100}%`,
                    top: `${shown.y1 * 100}%`,
                    width: `${(shown.x2 - shown.x1) * 100}%`,
                    height: `${(shown.y2 - shown.y1) * 100}%`,
                  }}
                />
              )}
              {!shown && (
                <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                  <span className="flex items-center gap-2 rounded-full bg-bf-surface/90 px-3 py-1.5 text-xs text-bf-text">
                    <Square size={13} /> Drag to draw the area to search
                  </span>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
